// "Report to compliance" — the universal intake modal opened from the
// loan header's overflow menu. The fast path is category + description +
// submit; suggested severity and a supporting document are optional so a
// non-compliance reporter can file in seconds. Uploads reuse the Legal Ops
// evidence pattern (loan entity tree + documentsApi.upload).

import { useState } from "react";
import { Button, FormField } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { documentsApi } from "@/api/documents";
import { foldersApi } from "@/api/folders";
import {
  COMPLIANCE_REPORT_CATEGORIES,
  COMPLIANCE_REPORT_SEVERITIES,
  type ComplianceIntakeReportSeverity,
} from "@/api/compliance/intakeReportTypes";
import { useCreateIntakeReport } from "@/hooks/compliance/useIntakeReports";

interface ReportToComplianceModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
}

export function ReportToComplianceModal({
  loanId,
  open,
  onClose,
}: ReportToComplianceModalProps) {
  const toast = useToast();
  const create = useCreateIntakeReport(loanId);

  const [category, setCategory] = useState<string>(
    COMPLIANCE_REPORT_CATEGORIES[0],
  );
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<"" | ComplianceIntakeReportSeverity>("");
  const [file, setFile] = useState<File | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCategory(COMPLIANCE_REPORT_CATEGORIES[0]);
    setDescription("");
    setSeverity("");
    setFile(null);
    setDocumentId(null);
    setError(null);
  };

  const surfaceError = (err: unknown, fallback: string) => {
    const msg =
      err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : fallback;
    setError(msg);
    toast.error(msg);
  };

  const handleFileChange = async (next: File | null) => {
    setError(null);
    setFile(next);
    setDocumentId(null);
    if (!next) return;
    setUploading(true);
    try {
      let tree;
      try {
        tree = await foldersApi.entityTree("loan", loanId);
      } catch {
        tree = await foldersApi.ensureEntityTree("loan", loanId, null);
      }
      const doc = await documentsApi.upload({
        title: next.name,
        category: "ComplianceDocument",
        folderId: tree.id,
        file: next,
        description: "Report to compliance — supporting document",
        autoIngest: true,
      });
      setDocumentId(doc.id);
    } catch (err) {
      setFile(null);
      surfaceError(err, "Could not upload the document.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (!description.trim()) {
      setError("A description is required.");
      return;
    }
    try {
      await create.mutateAsync({
        category,
        description: description.trim(),
        suggestedSeverity: severity || null,
        documentId,
      });
      toast.success("Reported to compliance.");
      reset();
      onClose();
    } catch (err) {
      surfaceError(err, "Could not file the report.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Report to compliance"
      description="Flag a problem on this loan for the compliance attorney (B4) to triage."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={create.isPending || uploading}
            data-testid="report-compliance-submit"
          >
            {create.isPending ? "Reporting…" : "Report"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Category" required>
          <select
            className="border border-border-light rounded-md h-10 px-space-2 text-small bg-white-pure"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="report-compliance-category"
          >
            {COMPLIANCE_REPORT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="What did you notice?" required>
          <textarea
            className="min-h-[96px] px-space-2 py-space-1 border border-border-light rounded-md text-small w-full"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the problem — B4 triages every report."
            data-testid="report-compliance-description"
          />
        </FormField>

        <FormField label="Suggested severity (optional)">
          <select
            className="border border-border-light rounded-md h-10 px-space-2 text-small bg-white-pure"
            value={severity}
            onChange={(e) =>
              setSeverity(e.target.value as "" | ComplianceIntakeReportSeverity)
            }
            data-testid="report-compliance-severity"
          >
            <option value="">Let B4 decide</option>
            {COMPLIANCE_REPORT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormField>

        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">
            Supporting document (optional)
          </span>
          <input
            type="file"
            onChange={(e) => void handleFileChange(e.target.files?.[0] ?? null)}
            data-testid="report-compliance-file"
          />
          {uploading && (
            <p className="text-caption text-gray-mid m-0">Uploading…</p>
          )}
          {file && documentId && (
            <p className="text-caption text-status-success m-0">
              Uploaded {file.name}
            </p>
          )}
        </div>

        {error && (
          <p
            className="text-small text-status-error m-0"
            role="alert"
            data-testid="report-compliance-error"
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
